function pickFirst(...values) {
  return values.find((value) => value !== undefined && value !== null && String(value).trim() !== '');
}

function normalizeLeads(payload) {
  if (Array.isArray(payload?.leads)) {
    return payload.leads;
  }

  if (payload?.leads && typeof payload.leads === 'object') {
    return Object.values(payload.leads);
  }

  if (Array.isArray(payload)) {
    return payload;
  }

  return [];
}

function normalizeBoolean(value) {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    return value === 1;
  }

  const normalized = String(value || '').trim().toLowerCase();
  return ['true', '1', 'yes', 'on'].includes(normalized);
}

function formatDateTime(value) {
  const raw = String(value || '').trim();
  if (!raw) {
    return '-';
  }

  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) {
    return raw;
  }

  return date.toLocaleString();
}

function normalizeString(value) {
  return String(value || '').trim();
}

function buildLeadName(lead) {
  const firstName = pickFirst(lead?.firstName, lead?.first_name);
  const lastName = pickFirst(lead?.lastName, lead?.last_name);
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
  return fullName || pickFirst(lead?.email, lead?.phone, lead?.uid, lead?.id) || '-';
}

function buildAssignedToName(lead) {
  const assigned = lead?.assigned || {};
  const firstName = pickFirst(assigned?.firstName, assigned?.first_name);
  const lastName = pickFirst(assigned?.lastName, assigned?.last_name);
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
  return fullName || pickFirst(
    assigned?.name,
    assigned?.full_name,
    assigned?.username,
    assigned?.emailMasked,
    assigned?.email_masked,
    assigned?.uid,
    assigned?.id,
  ) || '-';
}

function buildMatchedProfilePath(uid) {
  const normalizedUid = normalizeString(uid);
  return normalizedUid ? `/profiles/${encodeURIComponent(normalizedUid)}` : '';
}

function looksLikeEmail(value) {
  const normalized = normalizeString(value);
  return normalized.includes('@') && normalized.includes('.');
}

function buildMatchedProfileName(lead) {
  const name = normalizeString(lead?.matchedProfileName);
  const maskedEmail = normalizeString(lead?.matchedProfileEmailMasked);
  if (looksLikeEmail(name)) {
    return maskedEmail;
  }

  return name;
}

function normalizeCountry(value) {
  return normalizeString(value).toUpperCase();
}

function normalizeLanguage(value) {
  return normalizeString(value).toLowerCase();
}

function pushFilter(filterBy, field, value, normalize = normalizeString) {
  const normalized = normalize(value);
  if (!normalized) {
    return;
  }

  filterBy.push({ field, filter: normalized });
}

export function mapLeadsResponseToDto(payload) {
  return normalizeLeads(payload).map((lead, index) => {
    const id = normalizeString(pickFirst(lead?.uid, lead?.id));
    const matchedProfileUid = normalizeString(lead?.matchedProfileUid);
    const matchedProfileName = buildMatchedProfileName(lead);
    const created = pickFirst(lead?.created, lead?.createdAt, lead?.created_at);
    const source = {
      ...lead,
      matchedProfileName,
    };

    return {
      id,
      key: id || `lead-${index}`,
      name: buildLeadName(lead),
      email: pickFirst(lead?.email) || '-',
      phone: pickFirst(lead?.phone) || '-',
      country: pickFirst(lead?.country) || '-',
      language: pickFirst(lead?.language) || '-',
      status: pickFirst(lead?.status) || '-',
      collectionSource: pickFirst(lead?.collectionSource) || '-',
      collectionContext: pickFirst(lead?.collectionContext) || '',
      lawfulBasis: pickFirst(lead?.lawfulBasis) || '-',
      consentStatus: pickFirst(lead?.consentStatus) || '-',
      marketingConsentEmail: normalizeBoolean(lead?.marketingConsentEmail),
      marketingConsentPhone: normalizeBoolean(lead?.marketingConsentPhone),
      privacyNoticeStatus: pickFirst(lead?.privacyNoticeStatus) || '-',
      privacyNoticeSentAt: pickFirst(lead?.privacyNoticeSentAt) || '',
      doNotContact: normalizeBoolean(lead?.doNotContact),
      assignedToId: normalizeString(pickFirst(lead?.assigned?.uid, lead?.assigned?.id)),
      assignedToName: buildAssignedToName(lead),
      isMatchedProfile: normalizeBoolean(lead?.isMatchedProfile),
      matchedProfileUid,
      matchedProfileName,
      matchedProfileEmailMasked: pickFirst(lead?.matchedProfileEmailMasked) || '',
      matchedProfilePath: buildMatchedProfilePath(matchedProfileUid),
      created: created || '',
      createdAt: formatDateTime(created),
      source,
    };
  });
}

export function buildLeadsRequestDto(filters = {}, sort = {}, pagination = {}) {
  const filterBy = [];

  pushFilter(filterBy, 'status', filters.status);
  pushFilter(filterBy, 'email', filters.email);
  pushFilter(filterBy, 'firstName', filters.firstName);
  pushFilter(filterBy, 'lastName', filters.lastName);
  pushFilter(filterBy, 'country', filters.country, normalizeCountry);
  pushFilter(filterBy, 'phone', filters.phone);
  pushFilter(filterBy, 'language', filters.language, normalizeLanguage);
  pushFilter(filterBy, 'collectionSource', filters.collectionSource);
  pushFilter(filterBy, 'lawfulBasis', filters.lawfulBasis);
  pushFilter(filterBy, 'consentStatus', filters.consentStatus);
  pushFilter(filterBy, 'privacyNoticeStatus', filters.privacyNoticeStatus);

  if (filters.doNotContact === true) {
    filterBy.push({ field: 'doNotContact', filter: 'true' });
  }

  const dto = {
    limit: Number.isInteger(pagination?.limit) && pagination.limit > 0 ? pagination.limit : 20,
    offset: Number.isInteger(pagination?.offset) && pagination.offset > 0 ? pagination.offset : 0,
  };

  if (filters.assignedOnly === true) {
    dto.assignedOnly = true;
  }

  if (filters.matchedOnly === true) {
    dto.matchedOnly = true;
  }

  if (filterBy.length > 0) {
    dto.filterBy = filterBy;
  }

  const sortBy = normalizeString(pickFirst(sort?.field, sort?.sortBy));
  const sortOrder = normalizeString(pickFirst(sort?.order, sort?.sortOrder));
  if (sortBy) {
    dto.sortBy = sortBy;
  }
  if (sortOrder) {
    dto.sortOrder = sortOrder;
  }

  return dto;
}
